import { makeTheme } from 'dripsy';
import merge from 'lodash.merge';
import { base } from './base';
import { webFont } from './webFont';
const COLOR_BLACK = '#050400';
const COLOR_WHITE = '#FFF';
const COLOR_GRAY = '#EEEBE6';
const COLOR_GRAY_LIGHT = '#F4F3EF';
const COLOR_GRAY_LIGHTEST = '#F9F9F7';
const COLOR_BROWN_DARKER = '#2F2A23';
const COLOR_BROWN_DARK = '#3C3931';
const COLOR_BROWN = '#615E54';
const COLOR_BROWN_LIGHT = '#C0BAAE';
const COLOR_BROWN_LIGHTER = '#E6E2DA';
const COLOR_YELLOW_DARK = '#E0A100';
const COLOR_YELLOW = '#F3BB0B';
const COLOR_YELLOW_LIGHT = '#FBE8A8';
const COLOR_YELLOW_LIGHTEST = '#FFF5D6';
const COLOR_ORANGE_DARK = '#AE4B00';
const COLOR_ORANGE = '#FF760F';
const COLOR_ORANGE_LIGHT = '#FFCFAB';
const COLOR_ORANGE_LIGHTEST = '#FFECDE';
const COLOR_GREEN_DARK = '#617103';
const COLOR_GREEN = '#ACC806';
const COLOR_GREEN_LIGHT = '#E2ECA8';
const COLOR_GREEN_LIGHTEST = '#F4F7DF';
const COLOR_BLUE_DARK = '#1868C7';
const COLOR_BLUE = '#589FF1';
const COLOR_BLUE_LIGHT = '#B3DBFF';
const COLOR_BLUE_LIGHTEST = '#E5F4FF';
const COLOR_RED_DARK = '#8B1407';
const COLOR_RED = '#F4230B';
const COLOR_RED_LIGHT = '#FFD6D1';
const COLOR_RED_LIGHTEST = '#FFF3F2';
const rootFontName = 'GreycliffCF';
const theme = makeTheme({
    colors: {
        text: COLOR_BLACK,
        background: COLOR_WHITE,
        backgroundMedium: COLOR_GRAY,
        backgroundWeak: COLOR_GRAY_LIGHT,
        backgroundWeakest: COLOR_GRAY_LIGHTEST,
        border: COLOR_GRAY,
        primaryBackground: COLOR_YELLOW,
        primaryBackgroundHover: COLOR_YELLOW_DARK,
        primaryBackgroundActive: COLOR_YELLOW_LIGHT,
        primaryBackgroundWeak: COLOR_YELLOW_LIGHTEST,
        primaryBorder: COLOR_YELLOW,
        secondaryText: COLOR_BROWN,
        secondaryBackground: COLOR_BROWN_DARK,
        secondaryBackgroundHover: COLOR_BROWN_DARKER,
        secondaryBackgroundActive: COLOR_BROWN_LIGHT,
        secondaryBackgroundDisabled: COLOR_BROWN_LIGHTER,
        secondaryBorder: COLOR_BROWN_DARK,
        secondaryBorderDisabled: COLOR_BROWN_LIGHTER,
        secondaryIcon: COLOR_BROWN_DARK,
        secondaryIconDisabled: COLOR_BROWN_LIGHTER,
        warningBackground: COLOR_ORANGE,
        warningBackgroundHover: COLOR_ORANGE_DARK,
        warningBackgroundActive: COLOR_ORANGE_LIGHT,
        warningBackgroundWeak: COLOR_ORANGE_LIGHTEST,
        warningBorder: COLOR_ORANGE,
        warningIcon: COLOR_ORANGE,
        successBackground: COLOR_GREEN,
        successBackgroundHover: COLOR_GREEN_DARK,
        successBackgroundActive: COLOR_GREEN_LIGHT,
        successBackgroundWeak: COLOR_GREEN_LIGHTEST,
        successBorder: COLOR_GREEN,
        successIcon: COLOR_GREEN,
        infoBackground: COLOR_BLUE,
        infoBackgroundHover: COLOR_BLUE_DARK,
        infoBackgroundActive: COLOR_BLUE_LIGHT,
        infoBackgroundWeak: COLOR_BLUE_LIGHTEST,
        infoBorder: COLOR_BLUE,
        infoIcon: COLOR_BLUE,
        errorText: COLOR_RED,
        errorBackground: COLOR_RED,
        errorBackgroundHover: COLOR_RED_DARK,
        errorBackgroundActive: COLOR_RED_LIGHT,
        errorBackgroundWeak: COLOR_RED_LIGHTEST,
        errorBorder: COLOR_RED,
        errorIcon: COLOR_RED,
    },
    customFonts: {
        [rootFontName]: {
            default: rootFontName,
            normal: rootFontName,
            500: webFont('GreycliffCF-Medium'),
            600: webFont('GreycliffCF-DemiBold'),
            700: webFont('GreycliffCF-Bold'),
        },
    },
    fonts: {
        root: rootFontName,
        italic: webFont('GreycliffCF-RegularOblique'),
        boldItalic: webFont('GreycliffCF-BoldOblique'),
    },
    text: {
        displayLarge: {
            fontWeight: 'bold',
        },
        displayMedium: {
            fontWeight: 'bold',
        },
        displaySmall: {
            fontWeight: 'bold',
        },
        headingLarge: {
            fontWeight: 'bold',
        },
        headingMedium: {
            fontWeight: 'bold',
        },
        headingSmall: {
            fontWeight: 'bold',
        },
    },
    buttons: {
        primary: {
            borderRadius: 'default',
        },
        secondary: {
            borderRadius: 'default',
            color: 'background',
            $$active: {
                color: 'text',
            },
        },
        tertiary: {
            color: 'text',
            borderRadius: 'default',
            $$hover: {
                color: 'background',
            },
            $$active: {
                color: 'text',
            },
        },
        ghost: {
            color: 'text',
            borderRadius: 'default',
            $$hover: {
                color: 'background',
            },
            $$active: {
                color: 'text',
            },
        },
        slay: {
            borderRadius: 'default',
        },
    },
    tag: {
        borderRadius: 'default',
        $$hover: {
            color: 'background',
            backgroundColor: 'secondaryBackgroundHover',
        },
    },
    alerts: {
        warning: {
            borderRadius: 'default',
        },
        question: {
            borderRadius: 'default',
        },
        error: {
            borderRadius: 'default',
        },
        success: {
            borderRadius: 'default',
        },
        info: {
            borderRadius: 'default',
        },
    },
    hotspot: {
        default: {
            borderRadius: 'default',
        },
        active: {
            borderRadius: 'default',
            backgroundColor: 'primaryBackgroundWeak',
        },
        error: {
            borderRadius: 'default',
        },
        disabled: {
            borderRadius: 'default',
        },
    },
    tooltip: {
        borderRadius: 'subtle',
        text: {
            color: 'background',
        },
    },
    forms: {
        input: {
            borderRadius: 'default',
        },
        select: {
            borderRadius: 'default',
        },
        textarea: {
            borderRadius: 'default',
        },
        checkbox: {
            borderRadius: 'subtle',
            $$checked: {
                color: 'background',
            },
        },
    },
});
export const astronaut = merge({}, base, theme);
//# sourceMappingURL=astronaut.js.map